import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SearchComponent from '../../components/ui/SearchComponent';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BusinessCard from './components/BusinessCard';
import FilterPanel from './components/FilterPanel';
import BusinessDetailModal from './components/BusinessDetailModal';

const BusinessDirectoryPortal = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBusinessDetail, setShowBusinessDetail] = useState(false);

  const breadcrumbItems = [
    { label: 'Business Directory', path: '/business-directory-portal' }
  ];

  // Business categories for filtering
  const businessCategories = [
    { value: 'all', label: 'All Categories' },
    { value: 'technology', label: 'Technology' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'energy', label: 'Energy' },
    { value: 'water_management', label: 'Water Management' },
    { value: 'waste_management', label: 'Waste Management' },
    { value: 'governance', label: 'Governance' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'safety_security', label: 'Safety & Security' },
    { value: 'economic_development', label: 'Economic Development' }
  ];

  // Fetch businesses from database
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase?.from('businesses')?.select(`
            *,
            owner:owner_id(
              full_name,
              email,
              profile_image_url
            )
          `)?.order('is_featured', { ascending: false })?.order('created_at', { ascending: false });

        if (error) {
          setError(error?.message);
          console.error('Error fetching businesses:', error);
        } else {
          setBusinesses(data || []);
          setFilteredBusinesses(data || []);
        }
      } catch (err) {
        setError(err?.message);
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, []);

  // Filter businesses based on search and filters
  useEffect(() => {
    let filtered = [...businesses];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(business =>
        business?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        business?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        business?.services?.some(service => 
          service?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        ) ||
        business?.technologies?.some(tech => 
          tech?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        )
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(business => 
        business?.category === selectedCategory
      );
    }

    // Location filter
    if (selectedLocation !== 'all') {
      filtered = filtered?.filter(business => 
        business?.city?.toLowerCase() === selectedLocation?.toLowerCase() ||
        business?.country?.toLowerCase() === selectedLocation?.toLowerCase()
      );
    }

    setFilteredBusinesses(filtered);
  }, [businesses, searchQuery, selectedCategory, selectedLocation]);

  // Get unique locations for filter dropdown
  const locations = [
    { value: 'all', label: 'All Locations' },
    ...Array.from(new Set(
      businesses?.map(b => b?.city)?.filter(Boolean)
    ))?.map(city => ({ value: city?.toLowerCase(), label: city }))
  ];

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
    setShowBusinessDetail(true);
    
    // Update view count
    updateViewCount(business?.id);
  };

  const updateViewCount = async (businessId) => {
    try {
      const { error } = await supabase?.from('businesses')?.update({ 
          view_count: supabase?.raw('view_count + 1') 
        })?.eq('id', businessId);

      if (error) {
        console.error('Error updating view count:', error);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleConnectToBusiness = (business) => {
    // Navigate to communication hub with business context
    navigate('/communication-hub', { 
      state: { contactBusiness: business } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="entrepreneur" userName="Business User" />
        <main className="container mx-auto px-4 py-6 max-w-7xl">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading business directory...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="entrepreneur" userName="Business User" />
        <main className="container mx-auto px-4 py-6 max-w-7xl">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-4" />
              <p className="text-error mb-2">Error loading directory</p>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole="entrepreneur" 
        userName="Business User"
        notifications={2}
      />
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Business Directory
              </h1>
              <p className="text-muted-foreground">
                Discover and connect with smart city solution providers
              </p>
            </div>
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => navigate('/business-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back to Dashboard
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/communication-hub')}
                iconName="MessageSquare"
                iconPosition="left"
                iconSize={16}
              >
                Messages
              </Button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <SearchComponent
                placeholder="Search businesses, services, or technologies..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
            <div className="flex items-center space-x-4">
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
                placeholder="Category"
                options={businessCategories}
                className="min-w-48"
              />
              <Select
                value={selectedLocation}
                onValueChange={setSelectedLocation}
                placeholder="Location"
                options={locations}
                className="min-w-48"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden"
              >
                <Icon name="Filter" size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <p className="text-muted-foreground">
              {filteredBusinesses?.length} businesses found
              {searchQuery && ` for "${searchQuery}"`}
            </p>
            {(selectedCategory !== 'all' || selectedLocation !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedLocation('all');
                }}
                iconName="X"
                iconPosition="left"
                iconSize={14}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Business Grid */}
        {filteredBusinesses?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredBusinesses?.map((business) => (
              <BusinessCard
                key={business?.id}
                business={business}
                onClick={() => handleBusinessClick(business)}
                onConnect={() => handleConnectToBusiness(business)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No businesses found
            </h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search criteria or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedLocation('all');
              }}
              iconName="RotateCcw"
              iconPosition="left"
              iconSize={16}
            >
              Reset Filters
            </Button>
          </div>
        )}

        {/* Mobile Filter Panel */}
        {showFilters && (
          <FilterPanel
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            selectedCategory={selectedCategory}
            selectedLocation={selectedLocation}
            onCategoryChange={setSelectedCategory}
            onLocationChange={setSelectedLocation}
            categories={businessCategories}
            locations={locations}
          />
        )}

        {/* Business Detail Modal */}
        {showBusinessDetail && selectedBusiness && (
          <BusinessDetailModal
            business={selectedBusiness}
            isOpen={showBusinessDetail}
            onClose={() => {
              setShowBusinessDetail(false);
              setSelectedBusiness(null);
            }}
            onConnect={() => handleConnectToBusiness(selectedBusiness)}
            onViewProfile={() => {
              // Navigate to business profile page
              navigate(`/business-profile/${selectedBusiness?.id}`);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default BusinessDirectoryPortal;